import { Routes } from "@blitzjs/next"
import {
  SAutoModal,
  SCard,
  SIcon,
  SIconButton,
  SIconType,
  SInputV2,
  STooltip,
} from "@superwall/legacy-ui"
import ApplicationIcon from "app/applications/components/ApplicationIcon"
import { useSession } from "app/authv2/use-session"
import useIsMobile from "app/core/hooks/useIsMobile"
import type { ApplicationSearchResult } from "app/api-utils/trpc/routers/blitzMigration/applications"
import useIsTouchDevice from "app/core/hooks/useIsTouchDevice"
import moment from "moment"
import { useRouter } from "next/router"
import React, { createContext, useContext, useEffect, useMemo, useState } from "react"
import { trpc } from "app/core/trpcClient"

const formatRevenue = (revenue: number): string => {
  if (revenue >= 1_000_000) {
    return `$${(revenue / 1_000_000).toFixed(1)}M`
  }
  if (revenue >= 1_000) {
    return `$${(revenue / 1_000).toFixed(0)}k`
  }
  return `$${revenue.toFixed(0)}`
}

const MAX_CLICKHOUSE_INT32 = 2_147_483_647

const parseInt32Filter = (value: string): number | undefined => {
  if (!/^\d+$/.test(value)) return undefined

  const parsed = parseInt(value, 10)
  return Number.isSafeInteger(parsed) && parsed <= MAX_CLICKHOUSE_INT32 ? parsed : undefined
}

interface ApplicationSearchBarProps {
  searchTerm: string
  onChange: (value: string) => void
  autoFocus?: boolean
}

export const ApplicationSearchBar: React.FC<ApplicationSearchBarProps> = ({
  searchTerm,
  onChange,
  autoFocus = false,
}) => {
  return (
    <div className="w-full grid grid-cols-[auto,1fr,auto] items-center gap-3 px-2">
      <SIcon type="search" size={16} className="opacity-30" />
      <SInputV2
        autoFocus={autoFocus}
        value={searchTerm}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search Apps"
        className="!text-lg !h-auto !border-none !rounded-none !bg-transparent !px-0 !py-0"
      />
    </div>
  )
}

interface ApplicationSearchResultsProps {
  onSelect?: (application: ApplicationSearchResult) => void
}

type SearchParams = {
  searchTerm: string
  fullSearchTerm: string
  organizationId?: number
  applicationId?: number
  user?: string
}

type ApplicationSearchContextType = {
  applications: ApplicationSearchResult[]
  isLoading: boolean
  isError: boolean
  searchParams: SearchParams
}

const ApplicationSearchContext = createContext<ApplicationSearchContextType | null>(null)

export const useApplicationSearch = () => {
  const context = useContext(ApplicationSearchContext)
  if (!context)
    throw new Error("useApplicationSearch must be used within ApplicationSearchProvider")
  return context
}

export const ApplicationSearchProvider: React.FC<{
  children: React.ReactNode
  searchTerm: string
}> = ({ children, searchTerm }) => {
  const searchParams = useMemo(() => {
    let s = searchTerm.trim()
    let output = {
      searchTerm: s,
      fullSearchTerm: s,
      organizationId: undefined as number | undefined,
      applicationId: undefined as number | undefined,
      user: undefined as string | undefined,
    }

    if (
      searchTerm.toLowerCase().includes("org:") ||
      searchTerm.toLowerCase().includes("app:") ||
      searchTerm.toLowerCase().includes("user:")
    ) {
      const parts = s.split(" ")
      parts.forEach((p) => {
        const part = p.trim()
        const lowerPart = part.toLowerCase()
        if (lowerPart.startsWith("org:")) {
          const organizationId = parseInt32Filter(part.slice(4).trim())
          if (organizationId) {
            output.organizationId = organizationId
            s = s.replace(part, "")
          }
        } else if (lowerPart.startsWith("app:")) {
          const applicationId = parseInt32Filter(part.slice(4).trim())
          if (applicationId) {
            output.applicationId = applicationId
            s = s.replace(part, "")
          }
        } else if (lowerPart.startsWith("user:")) {
          output.user = part.slice(5).trim() || undefined
          s = s.replace(part, "")
        }
      })
    }

    // regex an email, if it is an email, then set the user to the email and remove the email from the search term
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (emailRegex.test(s)) {
      output.user = s
      s = s.replace(s, "").trim()
    }

    const normalizedSearchTerm = s.toLowerCase().trim()
    output.searchTerm = normalizedSearchTerm
    output.fullSearchTerm = normalizedSearchTerm

    return output
  }, [searchTerm])

  const { data: applications } = trpc.blitzMigration.applications.searchApplications.useQuery(
    {
      organizationId: searchParams.organizationId,
      applicationId: searchParams.applicationId,
      user: searchParams.user,
      searchTerm: searchParams.searchTerm,
    },
    {
      suspense: false,
    }
  )

  const apps = useMemo(() => {
    return applications ?? []
  }, [applications])

  const value = {
    applications: apps,
    isLoading: !applications,
    isError: false,
    searchParams,
  }

  return (
    <ApplicationSearchContext.Provider value={value}>{children}</ApplicationSearchContext.Provider>
  )
}

export const ApplicationSearchResults: React.FC<ApplicationSearchResultsProps> = ({ onSelect }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const { applications: apps, isLoading, isError, searchParams } = useApplicationSearch()
  const { data: user } = useSession()
  const showApplicationIds = user?.role === "SUPERADMIN"

  useEffect(() => {
    const el = document.getElementById(`app-${apps?.[selectedIndex]?.id}`)
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })
    }
  }, [apps, selectedIndex])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(apps?.length - 1, prev + 1))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(0, prev - 1))
      } else if (e.key === "Enter") {
        e.preventDefault()
        const selectedApp = apps?.[selectedIndex]
        if (selectedApp) {
          onSelect?.(selectedApp)
        }
      } else if (e.key.match(/^[a-zA-Z0-9]$/)) {
        setSelectedIndex(0)
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [apps, selectedIndex, onSelect])

  return (
    <div className="h-[363px] gap-[2px] flex flex-col relative overflow-y-scroll overflow-x-hidden">
      <div
        className={`absolute top-[-1px] pointer-events-none left-0 right-0 ${
          isLoading ? "" : "opacity-0"
        } transition-all`}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="grid h-[56px] grid-cols-[auto,1fr] gap-4 items-center px-4">
            <div
              className={`bg-grayscale-light/80 h-[30px] w-[30px] rounded-lg ${
                isLoading ? "animate-pule" : ""
              }`}
            ></div>
            <div
              className={`bg-grayscale-light/80 h-[20px] rounded-full ${
                isLoading ? "animate-pule" : ""
              }`}
            ></div>
          </div>
        ))}
      </div>

      {apps.map((application, index) => (
        <div
          key={application.id}
          id={`app-${application.id}`}
          className={`py-3 grid grid-cols-[auto,1fr] items-center gap-4 !cursor-pointer  ${
            index == selectedIndex ? "bg-grayscale-light/80" : "hover:bg-grayscale-light/40"
          } rounded-lg ${
            index == Math.min(49, apps.length - 1) ? "rounded-bl-xl rounded-br-xl mb-2" : ""
          } px-4 transition-all`}
          onClick={() => onSelect?.(application)}
        >
          <ApplicationIcon size={30} application={application} showPlatform={true} />
          <div className="grid grid-cols-[1fr,auto] items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 transition-tranform">
              <div className="min-w-0 shrink truncate">
                <STooltip
                  underline={false}
                  placement="top-start"
                  zIndex={9999}
                  content={<p>Joined {moment.utc(application.createdAt).fromNow()}</p>}
                >
                  <span className="truncate">{application.name}</span>
                </STooltip>
              </div>

              {showApplicationIds && (
                <code className="shrink-0 rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[11px] leading-none text-white/45">
                  {application.id}
                </code>
              )}

              {application.integrated && (
                <div className="bg-green-400 h-[8px] w-[8px] rounded-full"></div>
              )}
              {application.revenue != null && application.revenue > 0 ? (
                <span className="text-[11px] opacity-40 ml-1 text-left">
                  {formatRevenue(application.revenue)} • last 7d
                </span>
              ) : (
                <div />
              )}
            </div>
            <div
              className={`flex flex-row items-center gap-2 transition-transform ${
                index === selectedIndex ? "" : "translate-x-[20px]"
              }`}
            >
              <STooltip
                underline={false}
                placement="top-end"
                zIndex={9999}
                content={<p>Org ID: {application.organization?.id}</p>}
              >
                <div className="max-w-[100px] md:max-w-[200px] truncate text-[13px] opacity-30">
                  {application.organization?.name ?? ""}
                </div>
              </STooltip>
              <SIcon
                type={SIconType.ArrowUpRight}
                size={12}
                className={`opacity-0 ${
                  index === selectedIndex ? "opacity-30 scale-[1]" : "scale-[0.2]"
                } transition-all mr-1 rotate-180`}
              />
            </div>
          </div>
        </div>
      ))}

      {isError && (
        <div className="w-full py-3 px-6">
          <p className="text-white">
            🤨<span>Yikes! Error loading apps</span>
          </p>
        </div>
      )}
      {!isError && !isLoading && apps.length == 0 && (
        <div className="w-full py-3 px-6">
          <div className="text-white/50">
            None found for {`"`}
            <span className="text-white/90">{searchParams.fullSearchTerm}</span>
            {`"`}
          </div>
        </div>
      )}
    </div>
  )
}

interface ApplicationSeachCard {
  onSelect?: (application: ApplicationSearchResult) => void
  className?: string
}

export const ApplicationSearchCard: React.FC<ApplicationSeachCard> = ({ onSelect, className }) => {
  const [searchTerm, setSearchTerm] = useState("")

  return (
    <ApplicationSearchProvider searchTerm={searchTerm}>
      <SCard
        className={`!my-0 ${className}`}
        header={
          <div className="px-6 py-4">
            <ApplicationSearchBar
              autoFocus={true}
              searchTerm={searchTerm}
              onChange={setSearchTerm}
            />
          </div>
        }
      >
        <div className="p-2 pb-0">
          <ApplicationSearchResults onSelect={onSelect} />
        </div>
      </SCard>
    </ApplicationSearchProvider>
  )
}

interface CommandBarButtonProps {
  onClick: () => void
  isVisible?: boolean
}

export const CommandBarButton: React.FC<CommandBarButtonProps> = ({
  onClick,
  isVisible = true,
}) => {
  return (
    <div
      onClick={onClick}
      style={{
        transition: "all 167ms cubic-bezier(0.33, 0.00, 0.00, 1.00)",
      }}
      className={`${
        !isVisible ? "hidden" : ""
      } cursor-pointer fixed z-[9999999] bottom-[20px] left-[20px]`}
    >
      {/* <div className=""> */}
      <SIconButton
        className="bg-grayscale-dark border border-1 border-border-light !h-[48px] !w-[48px] !rounded-full shadow-xl flex flex-row items-center justify-center active:!scale-[0.85] hover:!scale-[1.1] hover:!opacity-100 active:!opacity-100"
        icon={SIconType.Search}
        iconSize={20}
        hotkey={["cmd", "k"]}
        onClick={onClick}
      />
      {/* </div> */}
    </div>
  )
}

interface CommandBarModalProps {}

export const CommandBar: React.FC<CommandBarModalProps> = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const router = useRouter()

  const isTouchDevice = useIsTouchDevice()
  const isMobile = useIsMobile()

  const showCommandBar = useMemo(() => isTouchDevice || isMobile, [isTouchDevice, isMobile])

  const handleSelectApplication = (application: ApplicationSearchResult) => {
    router.push(Routes.ShowApplicationDashboardPage({ applicationId: application.id }).href)
    setIsOpen(false)
  }

  return (
    <ApplicationSearchProvider searchTerm={searchTerm}>
      <SAutoModal
        title={
          <ApplicationSearchBar
            searchTerm={searchTerm}
            onChange={setSearchTerm}
            autoFocus={isOpen}
          />
        }
        onOpen={() => {
          setIsOpen(true)
          setSearchTerm("")
        }}
        onClose={() => {
          setIsOpen(false)
        }}
        actions={[]}
        button={({ open }) => <CommandBarButton onClick={open} isVisible={showCommandBar} />}
      >
        {({ close }) => (
          <div className="w-[80vw] max-w-[650px]">
            <div className="bg-white/7 h-[1px] -mx-6  mt-2"></div>
            <div className="-mx-4 -mb-9 pt-2">
              <ApplicationSearchResults
                onSelect={(application) => {
                  handleSelectApplication(application)
                  close()
                }}
              />
            </div>
          </div>
        )}
      </SAutoModal>
    </ApplicationSearchProvider>
  )
}
